/* ============================================================
   ZNAKI · config.js
   Jedno źródło prawdy: paleta, wszystkie parametry instalacji
   oraz schemat kontrolek panelu.

   Wszystkie parametry działają NA ŻYWO (bez przebudowy sceny).
   Jednostki:
     vh   — wysokości viewportu (1.0 = pełny ekran w pionie)
     vmin — krótszy bok viewportu (rozmiary figur)
   ============================================================ */

/* --- paleta (próbkowana z wizualizacji koncepcji) ---------- */
export const PALETTE = {
  bg:     '#EBDFC3',   /* ecru — papier */
  bgDeep: '#E2D3B2',   /* cień papieru (mottling) */
  orange: '#F4761C',   /* pomarańcz sygnałowy */
  black:  '#12100A',   /* czerń o ciepłym odcieniu */
  white:  '#FFFFFF',
  taupe:  '#A28A69',   /* szarobrąz — figury widmowe (multiply) */
  ink:    '#171207',   /* interfejs */
};

/* --- parametry instalacji ---------------------------------- */
export const PARAMS = {

  /* Ekran wprowadzający i wejście tablicy (intro.js).
     Zmiany działają przy następnym wejściu na widok — sekwencja
     odgrywa się raz, na starcie. */
  intro: {
    textDelay:   0.30,  /* zwłoka przed pojawieniem się tekstu [s] */
    instrDelay:  0.50,  /* dalsza zwłoka animowanej dłoni [s] */
    ctaDelay:    0.50,  /* dalsza zwłoka przycisku "rozpocznij" [s] */
    fade:        0.80,  /* czas pojawiania się każdego z elementów [s] */
    figDist:     0.85,  /* skąd figury nadjeżdżają: dystans spoza kadru [vh] */
    startDur:    2.60,  /* czas wjazdu figur [s] */
    startDist:   0.45,  /* przesuw tablicy podczas wjazdu [vh] — "ktoś przewija stronę" */
    outDur:      2.20,  /* czas wyjazdu typografii poza kadr (w górę) [s] */
    footerDelay: 1.00,  /* przerwa po wjeździe, zanim odsłoni się stopka [s] */
    footerDur:   1.40,  /* czas wjazdu stopki [s] */
  },

  scroll: {
    wheelSpeed: 0.75,   /* czułość kółka / trackpada */
    dragSpeed:  1.25,   /* czułość przeciągania (mysz / dotyk) */
    ease:       0.07,   /* bezwładność: jak szybko scena goni scroll (mniej = leniwiej) */
    autoDrift:  0.05,   /* autonomiczny dryf tablicy po bezczynności [vh/s]; 0 = wył. */
    idleDelay:  6.0,    /* sekundy bezczynności przed dryfem */
  },

  comp: {
    /* kręgosłup figur ciężkich: odstępy gwarantują min. 2 na ekranie */
    heavyGapMin: 0.34,  /* min. odstęp pionowy między ciężkimi [vh] */
    heavyGapMax: 0.46,  /* maks. odstęp (≤ 0.5 ⇒ zawsze ≥ 2 widoczne) */
    sideAmp:     0.24,  /* amplituda odchylenia ciężkich od osi [vmin] */
    satMax:      7,     /* maks. satelitów (figur lekkich) przy jednej ciężkiej */
    freeMax:     3,     /* maks. wolnych figur lekkich na komórkę [1 vh] */
    density:     0.65,  /* gęstość figur lekkich 0–1 (maska na żywo) */
    heavyDensity: 0.65, /* gęstość figur ciężkich 0–1 (maska na żywo);
                           uwaga: wartości <1 znoszą gwarancję min. 2 w kadrze */
    scale:       1.28,  /* globalna skala wielkości figur */
    axisPull:    0.22,  /* grawitacja osi: 0 = swobodnie, 1 = wszystko na osi */
  },

  phase: {
    /* Fazy 1 i 3 wyzwala pozycja slotu na tablicy (progi niżej),
       ale animacja odgrywa się we własnym czasie figury —
       bazowo 0.2–1.5 s, niezależnie od tempa scrolla. */
    margin:    0.22,    /* zapas poza kadrem na podróż figur (culling) [vh] */
    enterAt:   1.03,    /* próg fazy 1: slot mija tę pozycję [vh od góry kadru] */
    exitAt:    0.10,    /* próg fazy 3: slot mija tę pozycję [vh od góry kadru] */
    durMulIn:  1.0,     /* mnożnik czasu wjazdu (1 = bazowe 0.2–1.5 s figury) */
    durMulOut: 1.0,     /* mnożnik czasu wyjazdu */
    entryDist: 0.17,    /* dystans wjazdu spoza kadru [vh] */
    entrySpread: 0.6,   /* rozrzut boczny toru wjazdu 0–1 */
    exitDist:  0.5,     /* dystans wystrzału przy wyjeździe [vh] */
    easePow:   4.5,     /* krzywa wjazdu: potęga ease-out (silne wytracanie);
                           3 ≈ power2.out, 4 ≈ power3.out, 5 ≈ power4.out (GSAP);
                           per figura wariacja ±25% */
  },

  motion: {
    idleAmp:   0.22,    /* amplituda ruchu własnego w fazie trwania (drżenie, dryf) */
    idleSpeed: 1.0,     /* tempo ruchu własnego */
    violence:  0.6,     /* gwałtowność: udział i temperament figur niespokojnych 0–1 */
    velReact:  0.6,     /* sprzężenie zwrotne: jak scroll pobudza figury 0–2 */
    depth:     0.11,   /* rozwarstwienie parallax figur lekkich (0–0.35) */
    heavyDepth: 0.38,   /* udział rozwarstwienia dla figur ciężkich (× depth) */
    tilt:      0.9,     /* przechył całej tablicy od prędkości scrolla 0–1 */
    cursorPar: 0.35,    /* parallax od kursora 0–1 */
  },

  /* Glitch dużej pomarańczy (figures.js + renderer.js).
     Rzadkie, krótkie rozdarcie sygnału na największych kołach:
     tarcza rozpada się na pionowe pasy, część gubi kolor.
     Wyzwalane losowo, osobno dla każdej tarczy w kadrze —
     ma być nagrodą za scrollowanie, nie stałym efektem.
     Czas trwania losuje się z przedziału durMin–durMax i to on rządzi
     dźwiękiem: próbka glitcha jest przycinana do tej długości
     (parametry glVolume/glOffset/glRelease w grupie `audio`).
     Liczba pasów losuje się osobno przy KAŻDYM glitchu (nie raz na
     figurę), więc ta sama tarcza raz rozpada się na kilka kafli,
     a raz na gęsty raster.
     Rysuje to shader (glitch-gl.js) na własnym, ukrytym płótnie
     WebGL; `noise` i `smear` działają tylko tam. Bez WebGL renderer
     wraca do wersji na prostokątach i te dwa parametry są nieczynne. */
  glitch: {
    rate:     0.08,   /* częstość: średnia liczba glitchy na sekundę na jedną tarczę,
                         liczona tylko wtedy, gdy jej środek jest w kadrze */
    minR:     0.18,   /* od jakiego promienia koło jest "duże" [vmin] */
    strength: 0.40,   /* siła rozjazdu pasów (ułamek promienia) 0–1 */
    speed:    35,     /* tempo przetasowań pasów [na sekundę] */
    durMin:   0.12,   /* najkrótszy glitch [s] */
    durMax:   0.80,   /* najdłuższy glitch [s] */
    slicesMin: 3,     /* najmniejsza liczba pionowych pasów — grube kafle */
    slicesMax: 12,   /* największa liczba pasów — im więcej, tym bliżej rastra TV;
                         każdy glitch losuje własną wartość z tego przedziału */
    mono:     0.00,   /* jaka część pasów gubi pomarańcz na rzecz czerni i bieli 0–1 */
    noise:    0.55,   /* faktura z poziomu piksela: rwana krawędź i wygryzione ziarno 0–1 */
    smear:    0.45,   /* widmowa smuga zostawiona przez przesunięty pas 0–1 */
  },

  look: {
    grain:     0.75,    /* ziarno papieru 0–1 */
    vignette:  0.5,     /* winieta 0–1 */
    mottle:    0.5,     /* nierówność papieru 0–1 */
    axisAlpha: 0.00,    /* widoczność osi środkowej 0–0.4 */
    ticks:     true,    /* podziałka na osi (linijka czytania) */
  },

  audio: {
    /* Tło: sekwencja czterech akordów (tlo_1…tlo_4) grana w kółko.
       Zawsze gra dokładnie jeden akord — zmienia się tylko jego poziom.
       Następny akord odpala się w chwili, gdy tablica RUSZA po postoju
       na tyle długim, że tło zdążyło wybrzmieć (bgQuiet) — zmiana zawsze
       wypada w ciszy i słychać plik od początku. Patrz audio/background.js. */
    master:      0.80,  /* ogólna głośność instalacji 0–1 */
    bgVolume:    0.26,  /* głośność tła — jedno pokrętło do szybkiej regulacji 0–1 */
    bgVolMin:    0.00,  /* poziom tła przy nieruchomej tablicy */
    bgVolMax:    1.00,  /* poziom tła przy pełnej prędkości */
    bgVelRef:    1.20,  /* prędkość scrolla uznawana za pełną [vh/s] */
    bgAttack:    0.35,  /* stała czasowa narastania poziomu [s] */
    bgRelease:   1.20,  /* stała czasowa opadania [s] — wyciszenie po zatrzymaniu */
    bgFadeIn:    0.10,  /* wejście nowego akordu [s] — zmiana wypada w ciszy,
                           więc narastanie robi poziom magistrali (bgAttack);
                           dłuższa wartość przenosi krzyżowanie akordów w głośną
                           fazę po ruszeniu tablicy i wtedy przejście SŁYCHAĆ */
    bgFadeOut:   0.12,  /* wyjście poprzedniego akordu [s] — tylko zabezpieczenie
                           przed trzaskiem; w chwili zmiany tło i tak milczy */
    bgOverlap:   2.00,  /* zakładka przy przedłużaniu akordu (plik na siebie) [s] */
    bgArmLevel:  0.12,  /* ruch powyżej tego progu liczy się jako "tablica jedzie" */
    bgStallTime: 0.50,  /* bezruch, po którym postój zaczyna się liczyć [s] */
    bgQuiet:     0.12,  /* poziom, poniżej którego tło uznajemy za wybrzmiałe —
                           dopiero taki postój odkłada zmianę akordu; wyżej
                           przerwa w przewijaniu przełączałaby harmonię w środku
                           ruchu, na słyszalnym poziomie */
    bgMinChord:  2.00,  /* minimalny czas jednego akordu [s] — tylko bezpiecznik,
                           zmiana i tak wypada w ciszy */
    bgFilter:    0.00,  /* przymknięcie barwy tła przy bezruchu 0–1; 0 = filtr otwarty */
    bgCutoffMin: 700,   /* próg filtra przy pełnym bezruchu [Hz] (gdy bgFilter > 0) */

    /* Ziarna — dźwięki akcydentalne przypisane do figur (audio/accents.js).
       Bank tików wynika ŚCIŚLE z kształtu i koloru figury; figura bez
       własnego banku milczy. Każda brzmiąca figura ma swój łańcuch,
       więc filtr i panorama działają na całą figurę naraz. */
    acVolume:    0.89,  /* głośność ziaren 0–1 */
    acDensity:   1.00,  /* jaki odsetek figur w ogóle brzmi 0–1 */
    acPanWidth:  0.91,  /* szerokość panoramy 0–1 */
    acGainMin:   0.35,  /* udział najmniejszych figur w głośności */
    acTikiVol:   0.55,  /* waga banku tików */
    acExitVol:   0.55,  /* waga banku znikania */
    acClockVol:  1.50,  /* waga pętli zegarowych */
    acDetune:    0.01,  /* mikrowariacja wysokości ± (0 = wysokości dokładne) */
    acMinGap:    0.035, /* minimalny odstęp między ziarnami [s] */
    acFigGap:    0.09,  /* minimalny odstęp tików jednej figury [s] */
    acMaxGrains: 12,    /* maks. ziaren brzmiących jednocześnie */
    acMaxClocks: 3,     /* maks. pętli zegarowych jednocześnie */
    acCutoff:    0,     /* wspólny filtr na figurach [Hz]; 0 = otwarty */
    acDepthTone: 0.28,  /* przymknięcie barwy figur w głębi 0–1 */

    /* Rozdarcie sygnału na dużej pomarańczy (audio/accents.js).
       Próbki od autora są dłuższe niż sam efekt, więc gramy je przycięte
       do wylosowanego czasu glitcha (spec.glitch.dur) i od losowego
       miejsca — z pięciu plików wychodzi wtedy sporo różnych zdarzeń.
       Glitch gra także na tarczach, które nie mają własnego głosu
       (acDensity): jest za rzadki, żeby wypadało go zgubić. */
    glVolume:    0.70,  /* waga banku glitcha */
    glOffset:    0.40,  /* maks. losowe przesunięcie startu próbki [s] */
    glRelease:   0.05,  /* wyciszenie na końcu przyciętej próbki [s] —
                           bez niego cięcie zostawia trzask */

    /* Fale — przeciągły dźwięk pod powolnym kołysaniem patyczków
       i prostokątów (audio/accents.js), przeciwieństwo tików.
       Kolejne figury dostają KOLEJNE pliki banku (fala_1, fala_2, …),
       więc z ich pojawiania się splata się sugestia melodii — dlatego
       flMinGap i flMaxVoices trzymają linię rzadko, żeby dało się ją
       usłyszeć jako melodię, a nie akord.
       Głośność bierze się z tego, jak bardzo figura rzuca się w oczy:
       ile faluje (amplituda ruchu własnego — `drift` kołysze się kilka
       razy szerzej niż `calm`) i jak duża jest na ekranie. Ledwie
       drgający patyczek dostaje flVolMin, szeroko falujący panel przez
       pół kadru — flVolMax. */
    flVolMin:    0.20,  /* waga fali pod figurą ledwie falującą i drobną */
    flVolMax:    0.80,  /* waga fali pod figurą dużą i szeroko kołyszącą się */
    flChance:    0.55,  /* jaka część falujących figur dostaje dźwięk 0–1 */
    flOverlap:   1.20,  /* przedłużanie fali: zakładka próbki na siebie [s].
                           Figura potrafi falować dłużej niż trwa plik, więc
                           dźwięk nawijamy w kółko — nie natywną pętlą, bo
                           próbki nie są cięte na styk, tylko zakładką, tak
                           jak akordy tła. 0 = zagraj raz i zamilcz. */
    flFadeIn:    0.35,  /* narastanie fali [s] */
    flRelease:   0.60,  /* gaśnięcie, gdy figura rusza do wyjścia [s] */
    flMinGap:    0.35,  /* minimalny odstęp między wejściami fal [s] */
    flMaxVoices: 4,     /* maks. fal brzmiących jednocześnie */

    /* Przytrzymanie — ściśnięcie figur do osi (audio/index.js).
       Całość wchodzi pod filtr; sweep prowadzi to samo st.hold, co zbieg
       figur, więc tempo zgadza się w obie strony: przy naciśnięciu
       i przy puszczeniu. Tło samo z siebie w tym momencie milknie
       (background.js) — tak jak chciał autor dźwięku. */
    holdCutoff:  320,   /* próg filtra na całości przy pełnym przytrzymaniu [Hz] */
    holdQ:       2.00,  /* rezonans filtra przy pełnym przytrzymaniu */
  },
};

/* kopia domyślnych do resetu */
export const DEFAULTS = JSON.parse(JSON.stringify(PARAMS));

/* --- dostęp po ścieżce "grupa.pole" ------------------------- */
export const getParam = (path) => {
  const [g, k] = path.split('.');
  return PARAMS[g][k];
};
export const setParam = (path, v) => {
  const [g, k] = path.split('.');
  PARAMS[g][k] = v;
};
export const resetParams = () => {
  const d = JSON.parse(JSON.stringify(DEFAULTS));
  for (const g in d) for (const k in d[g]) PARAMS[g][k] = d[g][k];
};

/* --- eksport ustawień (panel dev) --------------------------- */

/* tylko to, co odbiega od domyślnych — gotowe do przepisania do PARAMS */
export const diffParams = () => {
  const out = {};
  for (const g in PARAMS)
    for (const k in PARAMS[g])
      if (PARAMS[g][k] !== DEFAULTS[g][k]) out[g + '.' + k] = PARAMS[g][k];
  return out;
};

/* pełna migawka ustawień do zapisu w pliku JSON */
export const exportParams = (meta = {}) => ({
  format:     'znaki-params',
  version:    1,
  edition:    '2026',
  exportedAt: new Date().toISOString(),
  ...meta,
  changed: diffParams(),
  params:  JSON.parse(JSON.stringify(PARAMS)),
});

/* --- schemat panelu: wybrane parametry o największym wpływie */
export const CONTROL_SCHEMA = [
  {
    group: 'Wejście',
    items: [
      { path: 'intro.figDist',     label: 'Dystans wjazdu figur', min: 0,   max: 2, step: 0.05 },
      { path: 'intro.startDur',    label: 'Czas wjazdu [s]',      min: 0.5, max: 6, step: 0.1 },
      { path: 'intro.startDist',   label: 'Przesuw tablicy',      min: 0,   max: 2, step: 0.05 },
      { path: 'intro.outDur',      label: 'Wyjście tekstu [s]',   min: 0.5, max: 6, step: 0.1 },
      { path: 'intro.footerDelay', label: 'Zwłoka stopki [s]',    min: 0,   max: 4, step: 0.1 },
    ],
  },
  {
    group: 'Ruch tablicy',
    items: [
      { path: 'scroll.wheelSpeed', label: 'Czułość scrolla',   min: 0.3,  max: 3.0,  step: 0.05 },
      { path: 'scroll.ease',       label: 'Bezwładność',       min: 0.02, max: 0.20, step: 0.005 },
      { path: 'scroll.autoDrift',  label: 'Autodryf',          min: 0,    max: 0.6,  step: 0.01 },
      { path: 'motion.tilt',       label: 'Przechył tablicy',  min: 0,    max: 1,    step: 0.01 },
    ],
  },
  {
    group: 'Kompozycja',
    items: [
      { path: 'comp.density',      label: 'Gęstość lekkich',  min: 0,    max: 1,    step: 0.01 },
      { path: 'comp.heavyDensity', label: 'Gęstość ciężkich', min: 0.2,  max: 1,    step: 0.01 },
      { path: 'comp.scale',        label: 'Skala figur',      min: 0.5,  max: 1.6,  step: 0.01 },
      { path: 'comp.axisPull',     label: 'Grawitacja osi',   min: 0,    max: 1,    step: 0.01 },
      { path: 'motion.depth',      label: 'Rozwarstwienie',   min: 0,    max: 0.35, step: 0.005 },
    ],
  },
  {
    group: 'Fazy ruchu figur lekkich',
    items: [
      { path: 'phase.durMulIn',  label: 'Czas wjazdu ×',     min: 0.25, max: 3,   step: 0.05 },
      { path: 'phase.durMulOut', label: 'Czas wyjazdu ×',    min: 0.25, max: 3,   step: 0.05 },
      { path: 'phase.entryDist', label: 'Dystans wjazdu',    min: 0.1,  max: 1.2, step: 0.01 },
      { path: 'phase.easePow',   label: 'Krzywa wjazdu',     min: 2,    max: 6,   step: 0.1 },
    ],
  },
  {
    group: 'Temperament',
    items: [
      { path: 'motion.idleAmp',  label: 'Drżenie / dryf',       min: 0, max: 2.5, step: 0.01 },
      { path: 'motion.violence', label: 'Gwałtowność',          min: 0, max: 1,   step: 0.01 },
      { path: 'motion.velReact', label: 'Sprzężenie zwrotne',   min: 0, max: 2,   step: 0.01 },
    ],
  },
  {
    group: 'Glitch',
    items: [
      { path: 'glitch.rate',     label: 'Częstość [1/s]',   min: 0,    max: 3,    step: 0.01 },
      { path: 'glitch.minR',     label: 'Próg wielkości',   min: 0.10, max: 0.35, step: 0.005 },
      { path: 'glitch.strength', label: 'Siła rozjazdu',    min: 0,    max: 1,    step: 0.01 },
      { path: 'glitch.speed',    label: 'Tempo [1/s]',      min: 2,    max: 60,   step: 1 },
      { path: 'glitch.durMin',   label: 'Czas min. [s]',    min: 0.05, max: 2,    step: 0.01 },
      { path: 'glitch.durMax',   label: 'Czas maks. [s]',   min: 0.05, max: 4,    step: 0.01 },
      { path: 'glitch.slicesMin', label: 'Pasy min.',       min: 4,    max: 120,  step: 1 },
      { path: 'glitch.slicesMax', label: 'Pasy maks.',      min: 4,    max: 120,  step: 1 },
      { path: 'glitch.mono',     label: 'Monochromia',      min: 0,    max: 1,    step: 0.01 },
      { path: 'glitch.noise',    label: 'Faktura (shader)', min: 0,    max: 1,    step: 0.01 },
      { path: 'glitch.smear',    label: 'Smuga (shader)',   min: 0,    max: 1,    step: 0.01 },
      { path: 'audio.glVolume',  label: 'Dźwięk',           min: 0,    max: 1.5,  step: 0.05 },
      { path: 'audio.glOffset',  label: 'Dźwięk: start [s]', min: 0,   max: 2,    step: 0.05 },
      { path: 'audio.glRelease', label: 'Dźwięk: zejście [s]', min: 0, max: 0.4,  step: 0.01 },
    ],
  },
  {
    group: 'Materia',
    items: [
      { path: 'look.grain',     label: 'Ziarno',         min: 0, max: 1,   step: 0.01 },
      { path: 'look.axisAlpha', label: 'Widoczność osi', min: 0, max: 0.4, step: 0.005 },
    ],
  },
  {
    group: 'Dźwięk',
    items: [
      { path: 'audio.master',    label: 'Głośność ogólna',   min: 0,   max: 1,  step: 0.01 },
      { path: 'audio.bgVolume',  label: 'Głośność tła',      min: 0,   max: 1,  step: 0.01 },
      { path: 'audio.bgVelRef',  label: 'Próg pełnej prędkości', min: 0.3, max: 3, step: 0.05 },
      { path: 'audio.bgRelease', label: 'Wyciszanie tła [s]', min: 0.3, max: 4,  step: 0.05 },
      { path: 'audio.bgQuiet',   label: 'Próg ciszy tła',     min: 0,   max: 0.4, step: 0.01 },
      { path: 'audio.bgFadeIn',  label: 'Wejście akordu [s]', min: 0,   max: 2,  step: 0.05 },
      { path: 'audio.bgOverlap', label: 'Zakładka akordu [s]', min: 1,  max: 15, step: 0.5 },
      { path: 'audio.bgFilter',  label: 'Filtr przy bezruchu', min: 0,  max: 1,  step: 0.01 },
    ],
  },
  {
    group: 'Ziarna',
    items: [
      { path: 'audio.acVolume',    label: 'Głośność ziaren',  min: 0, max: 1,   step: 0.01 },
      { path: 'audio.acDensity',   label: 'Gęstość ziaren',   min: 0, max: 1,   step: 0.01 },
      { path: 'audio.acPanWidth',  label: 'Szerokość planu',  min: 0, max: 1,   step: 0.01 },
      { path: 'audio.acTikiVol',   label: 'Tiki',             min: 0, max: 1.5, step: 0.05 },
      { path: 'audio.acExitVol',   label: 'Znikanie',         min: 0, max: 1.5, step: 0.05 },
      { path: 'audio.acClockVol',  label: 'Zegary',           min: 0, max: 1.5, step: 0.05 },
      { path: 'audio.acDepthTone', label: 'Barwa w głębi',    min: 0, max: 1,   step: 0.01 },
      { path: 'audio.acCutoff',    label: 'Filtr figur [Hz]', min: 0, max: 12000, step: 100 },
    ],
  },
  {
    group: 'Fale',
    items: [
      { path: 'audio.flVolMin',    label: 'Głośność min.',   min: 0, max: 1.5, step: 0.05 },
      { path: 'audio.flVolMax',    label: 'Głośność maks.',  min: 0, max: 1.5, step: 0.05 },
      { path: 'audio.flChance',    label: 'Gęstość',         min: 0, max: 1,   step: 0.01 },
      { path: 'audio.flOverlap',   label: 'Zakładka [s]',    min: 0, max: 4,   step: 0.05 },
      { path: 'audio.flMinGap',    label: 'Odstęp [s]',      min: 0, max: 3,   step: 0.05 },
      { path: 'audio.flMaxVoices', label: 'Naraz',           min: 1, max: 12,  step: 1 },
      { path: 'audio.flFadeIn',    label: 'Narastanie [s]',  min: 0, max: 3,   step: 0.05 },
      { path: 'audio.flRelease',   label: 'Gaśnięcie [s]',   min: 0.05, max: 4, step: 0.05 },
    ],
  },
  {
    group: 'Przytrzymanie',
    items: [
      { path: 'audio.holdCutoff', label: 'Próg filtra [Hz]', min: 80,  max: 4000, step: 20 },
      { path: 'audio.holdQ',      label: 'Rezonans filtra',  min: 0.7, max: 8,    step: 0.1 },
    ],
  },
];
